import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

const API_PATH = environment.apiBaseUrl;

function withApiAuth(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

function markRetried(req: HttpRequest<unknown>): HttpRequest<unknown> {
  return req.clone({
    setHeaders: { 'x-api-auth-retried': '1' },
  });
}

function wasRetried(req: HttpRequest<unknown>): boolean {
  return req.headers.get('x-api-auth-retried') === '1';
}

function isApiRequest(req: HttpRequest<unknown>): boolean {
  return req.url.startsWith(API_PATH);
}

export const nexusAuthInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);

  return from(authService.getToken([environment.apiScope])).pipe(
    switchMap((token) => {
      const authedReq = token && isApiRequest(req) ? withApiAuth(req, token) : req;

      return next(authedReq);
    }),

    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }

      if (!isApiRequest(req) || err.status !== 401 || wasRetried(req)) {
        return throwError(() => err);
      }

      return from(authService.getToken([environment.apiScope])).pipe(
        switchMap((token) => {
          if (!token) return throwError(() => err);

          return next(withApiAuth(markRetried(req), token));
        }),
      );
    }),
  );
};
